import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, IconButton, Avatar, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import {
  ArrowBack as BackIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  ExitToApp as ExitIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Archive as ArchiveIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { ChatMessage, Conversation, TypingUser } from '../../types/chat';
import { UploadItem } from '../../contexts/UploadContext';
import MessageItem from './MessageItem';
import MessageInput from './MessageInput';
import TypingIndicator from './TypingIndicator';
import ChatAvatar from './ChatAvatar';
import UploadProgressBar from './UploadProgressBar';

interface MessageThreadProps {
  conversation: Conversation;
  messages: ChatMessage[];
  currentUserId: number;
  typingUsers: TypingUser[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  uploads?: UploadItem[];
  onSendMessage: (content: string, files: File[]) => Promise<void>;
  onDeleteMessage?: (messageId: number) => void;
  onLoadMore?: () => void;
  onTyping?: () => void;
  onBack?: () => void;
  onCancelUpload?: (uploadId: string) => void;
  showBackButton?: boolean;
  onOpenGroupSettings?: () => void;
  onLeaveGroup?: () => void;
  onToggleStar?: (conversationId: number) => void;
  onToggleArchive?: (conversationId: number) => void;
  onDeleteConversation?: (conversationId: number) => void;
  onRestoreConversation?: (conversationId: number) => void;
}

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

const MessageThread: React.FC<MessageThreadProps> = ({
  conversation,
  messages,
  currentUserId,
  typingUsers,
  loading = false,
  loadingMore = false,
  hasMore = false,
  uploads = [],
  onSendMessage,
  onDeleteMessage,
  onLoadMore,
  onTyping,
  onBack,
  onCancelUpload,
  showBackButton = false,
  onOpenGroupSettings,
  onLeaveGroup,
  onToggleStar,
  onToggleArchive,
  onDeleteConversation,
  onRestoreConversation,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef<number>(0);
  const prevConversationIdRef = useRef<number | null>(null);
  // Whether we've already done the initial jump-to-bottom for the current conversation
  const didInitialScrollRef = useRef<boolean>(false);
  // Whether the user is currently pinned to the bottom of the thread
  const isAtBottomRef = useRef<boolean>(true);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);

  const otherParticipant = conversation.other_participant;
  const isGroup = conversation.is_group;

  // Check if current user is admin
  const currentUserParticipant = conversation.participants.find(p => p.user_id === currentUserId);
  const isAdmin = currentUserParticipant?.is_admin || false;

  // Get online members count for groups
  const onlineMembersCount = isGroup
    ? conversation.participants.filter(p => p.user.is_online).length
    : 0;

  // Get display name
  const getDisplayName = () => {
    if (isGroup) {
      return conversation.group_name || 'Groupe sans nom';
    }
    return otherParticipant?.full_name || '';
  };

  // Get status text
  const getStatusText = () => {
    if (isGroup) {
      return `${conversation.participants.length} membres${onlineMembersCount > 0 ? ` • ${onlineMembersCount} en ligne` : ''}`;
    }
    return otherParticipant?.is_online ? 'En ligne' : 'Hors ligne';
  };

  const handleGroupMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setGroupMenuAnchor(event.currentTarget);
  };

  const handleGroupMenuClose = () => {
    setGroupMenuAnchor(null);
  };

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleToggleStar = () => {
    onToggleStar?.(conversation.id);
    handleActionsMenuClose();
  };

  const handleToggleArchive = () => {
    onToggleArchive?.(conversation.id);
    handleActionsMenuClose();
  };

  const handleDeleteConversation = () => {
    onDeleteConversation?.(conversation.id);
    handleActionsMenuClose();
  };

  const handleRestoreConversation = () => {
    onRestoreConversation?.(conversation.id);
    handleActionsMenuClose();
  };

  // Instantly pin the thread to the bottom (most reliable for the initial load)
  const jumpToBottom = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Scroll to bottom when conversation changes or new messages arrive
  useEffect(() => {
    const isNewConversation = prevConversationIdRef.current !== conversation.id;
    if (isNewConversation) {
      // New conversation: reset trackers and wait for its messages to arrive
      prevConversationIdRef.current = conversation.id;
      prevMessagesLengthRef.current = messages.length;
      didInitialScrollRef.current = false;
      isAtBottomRef.current = true;
    }

    const hasNewMessages = messages.length > prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;

    if (messages.length === 0) return;

    // First time this conversation actually has messages: jump instantly.
    // Use a double rAF so the jump runs after the message list has painted,
    // and re-pin once async content (avatars/images) finishes loading below.
    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      isAtBottomRef.current = true;
      requestAnimationFrame(() => requestAnimationFrame(jumpToBottom));
      return;
    }

    // Later messages: only follow the bottom if the user is already pinned there,
    // so we don't yank them away while reading older messages.
    if (hasNewMessages && isAtBottomRef.current) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [messages, conversation.id, jumpToBottom]);

  // Re-pin to the bottom when late-loading images grow the thread (first/uncached
  // open): img "load" doesn't bubble, so listen in the capture phase on the container.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onContentLoad = () => {
      if (isAtBottomRef.current) {
        jumpToBottom();
      }
    };
    container.addEventListener('load', onContentLoad, true);
    return () => container.removeEventListener('load', onContentLoad, true);
  }, [conversation.id, jumpToBottom]);

  // Handle scroll: track whether the user is pinned to the bottom + infinite loading
  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    isAtBottomRef.current = distanceFromBottom < 80;

    if (hasMore && !loadingMore && container.scrollTop === 0) {
      onLoadMore?.();
    }
  };

  // Group messages by sender to show/hide avatars
  const shouldShowAvatar = (index: number) => {
    if (index === 0) return true;
    const currentMsg = messages[index];
    const prevMsg = messages[index - 1];
    return currentMsg.sender_id !== prevMsg.sender_id;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'background.default',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {showBackButton && onBack && (
          <IconButton onClick={onBack} size="small">
            <BackIcon />
          </IconButton>
        )}

        {/* Group or 1-to-1 header */}
        {isGroup ? (
          <>
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={handleGroupMenuOpen}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: OUTLOOK_BLUE,
                }}
              >
                {conversation.group_avatar_url ? (
                  <img src={conversation.group_avatar_url} alt={conversation.group_name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <GroupIcon />
                )}
              </Avatar>
            </Box>
            <Box
              sx={{ flex: 1, cursor: 'pointer' }}
              onClick={handleGroupMenuOpen}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {getDisplayName()}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {getStatusText()}
              </Typography>
            </Box>

            {/* Group Menu */}
            <Menu
              anchorEl={groupMenuAnchor}
              open={Boolean(groupMenuAnchor)}
              onClose={handleGroupMenuClose}
              PaperProps={{
                sx: { minWidth: 200, borderRadius: 2 }
              }}
            >
              <MenuItem onClick={() => { handleGroupMenuClose(); onOpenGroupSettings?.(); }}>
                <ListItemIcon>
                  <PeopleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Voir les membres</ListItemText>
              </MenuItem>
              {isAdmin && (
                <MenuItem onClick={() => { handleGroupMenuClose(); onOpenGroupSettings?.(); }}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Paramètres du groupe</ListItemText>
                </MenuItem>
              )}
              <Divider />
              <MenuItem
                onClick={() => { handleGroupMenuClose(); onLeaveGroup?.(); }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <ExitIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Quitter le groupe</ListItemText>
              </MenuItem>
            </Menu>
          </>
        ) : otherParticipant ? (
          <>
            <ChatAvatar
              name={otherParticipant.full_name}
              isOnline={otherParticipant.is_online}
              lastSeenAt={otherParticipant.last_seen_at}
              size={40}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {otherParticipant.full_name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {getStatusText()}
              </Typography>
            </Box>
          </>
        ) : null}

        {/* Star and Actions buttons */}
        {(onToggleStar || onToggleArchive || onDeleteConversation || onRestoreConversation) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Star button */}
            {onToggleStar && (
              <IconButton
                size="small"
                onClick={() => onToggleStar(conversation.id)}
                sx={{ color: conversation.is_starred ? '#ffc107' : '#999' }}
              >
                {conversation.is_starred ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>
            )}

            {/* More actions menu */}
            <IconButton size="small" onClick={handleActionsMenuOpen}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={actionsMenuAnchor}
              open={Boolean(actionsMenuAnchor)}
              onClose={handleActionsMenuClose}
              PaperProps={{
                sx: { borderRadius: 2, minWidth: 180 }
              }}
            >
              {onToggleStar && (
                <MenuItem onClick={handleToggleStar}>
                  <ListItemIcon>
                    {conversation.is_starred ? (
                      <StarIcon fontSize="small" sx={{ color: '#ffc107' }} />
                    ) : (
                      <StarBorderIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText>
                    {conversation.is_starred ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                  </ListItemText>
                </MenuItem>
              )}
              {onToggleArchive && (
                <MenuItem onClick={handleToggleArchive}>
                  <ListItemIcon>
                    <ArchiveIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {conversation.is_archived ? 'Désarchiver' : 'Archiver'}
                  </ListItemText>
                </MenuItem>
              )}
              {onDeleteConversation && (
                <MenuItem onClick={handleDeleteConversation} sx={{ color: 'error.main' }}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Supprimer</ListItemText>
                </MenuItem>
              )}
              {onRestoreConversation && (
                <MenuItem onClick={handleRestoreConversation} sx={{ color: 'success.main' }}>
                  <ListItemIcon>
                    <RestoreIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText>Restaurer</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </Box>
        )}
      </Box>

      {/* Messages */}
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 2,
        }}
      >
        {loadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} sx={{ color: OUTLOOK_BLUE }} />
          </Box>
        )}

        {loading && messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress size={32} sx={{ color: OUTLOOK_BLUE }} />
          </Box>
        ) : messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              px: 2,
            }}
          >
            <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
              No messages yet
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 1 }}>
              Start the conversation by sending a message
            </Typography>
          </Box>
        ) : (
          messages.map((message, index) => (
            <MessageItem
              key={message.id}
              message={message}
              isOwnMessage={Number(message.sender_id) === Number(currentUserId)}
              showAvatar={shouldShowAvatar(index)}
              onDelete={onDeleteMessage}
            />
          ))
        )}

        {/* Typing indicator */}
        {typingUsers.length > 0 && (
          <TypingIndicator
            userName={typingUsers.map((u) => u.user_name).join(', ')}
          />
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Upload Progress */}
      {uploads.length > 0 && onCancelUpload && (
        <UploadProgressBar uploads={uploads} onCancel={onCancelUpload} />
      )}

      {/* Input */}
      <MessageInput
        onSend={onSendMessage}
        onTyping={onTyping}
        placeholder={`Message ${getDisplayName()}...`}
      />
    </Box>
  );
};

export default MessageThread;
